import { Component, ContentChild, Input, Output, EventEmitter, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Modal } from '../../modal';

@Component({
    standalone: false,
    selector: 'abs-account-settings',
    template: `
        <ng-template #defaultDeleteConfirmTemplate>
            <p>{{ 'GLOBAL.SETTINGS.ACCOUNT.DELETE.ARE_YOU_SURE' | translate }}</p>
        </ng-template>

        <ng-template #deleteModalContent>
            <ng-container *ngTemplateOutlet="deleteConfirmContent || defaultDeleteConfirmTemplate"></ng-container>
            <abs-reauth-prompt (reauthenticated)="onReauthenticated()"></abs-reauth-prompt>
            <form [formGroup]="confirmForm">
                <ion-input
                    formControlName="confirmText"
                    label="{{ 'GLOBAL.SETTINGS.ACCOUNT.DELETE.CONFIRM_LABEL' | translate }}"
                ></ion-input>
            </form>
        </ng-template>

        <ng-template #deleteButtonsTemplate>
            <ion-button color="danger" [disabled]="!deleteConfirmationEnabled" (click)="confirmDelete()">
                {{ 'GLOBAL.YES' | translate }}
            </ion-button>
            <ion-button (click)="cancelDelete()">{{ 'GLOBAL.NO' | translate }}</ion-button>
        </ng-template>

        <div class="buttons">
            <ion-button (click)="triggerLogout()">{{ 'GLOBAL.LOGIN.BUTTONS.LOGOUT' | translate }}</ion-button>
            <ion-button color="danger" (click)="showDeleteModal()">
                {{ 'GLOBAL.SETTINGS.ACCOUNT.DELETE.BUTTON' | translate }}
            </ion-button>
        </div>
    `,
    styles: [
        `
            .buttons {
                margin-left: 0;
                margin-right: 0;
            }
        `
    ]
})
export class AbsAccountSettingsComponent {
    @Input({ required: true }) logout!: () => void;
    @Output() deleteAccountConfirmed = new EventEmitter<void>();

    @ContentChild('deleteConfirmContent') deleteConfirmContent: TemplateRef<any>;
    @ViewChild('deleteModalContent') deleteModalContent: TemplateRef<any>;
    @ViewChild('deleteButtonsTemplate') deleteButtonsTemplate: TemplateRef<any>;

    reauthComplete = false;
    confirmForm = this.fb.group({
        confirmText: this.fb.control('')
    });

    constructor(
        private modal: Modal,
        private translateService: TranslateService,
        private fb: UntypedFormBuilder
    ) {}

    get deleteConfirmationEnabled(): boolean {
        const expected = this.translateService.instant('GLOBAL.SETTINGS.ACCOUNT.DELETE.CONFIRM_TEXT');
        return this.reauthComplete && this.confirmForm.value.confirmText === expected;
    }

    onReauthenticated() {
        this.reauthComplete = true;
    }

    triggerLogout() {
        this.logout();
    }

    showDeleteModal() {
        this.reauthComplete = false;
        this.confirmForm.reset();
        this.modal.show({
            props: {
                contentTemplate: this.deleteModalContent,
                buttonsTemplate: this.deleteButtonsTemplate,
                buttonsDirection: 'horizontal',
                title: this.translateService.instant('GLOBAL.SETTINGS.ACCOUNT.DELETE.TITLE')
            }
        });
    }

    confirmDelete() {
        if (!this.deleteConfirmationEnabled) {
            return;
        }
        this.modal.dismiss();
        this.deleteAccountConfirmed.emit();
    }

    cancelDelete() {
        this.reauthComplete = false;
        this.confirmForm.reset();
        this.modal.dismiss();
    }
}
